import { BarChartOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";

import { transactionFilter } from "../../api/TranscationApi"; // Adjust import path
import { useGlobalUI } from "../../context/GlobalUIProvider";
import { formatIdWithPrefix } from "../../Custom/Custom";
import useAuthService from "../../session/AuthService";
import AuthService from "../../session/AuthService";

const { Option } = Select;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export const StatusTag = ({ status }) => {
  const colors = {
    SUCCESS: "green",
    PENDING: "orange",
    FAILED: "red",
  };
  return (
    <Tag color={colors[status]}>
      <span
        style={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: "inherit",
          marginRight: 6,
        }}
      />
      {status}
    </Tag>
  );
};

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const userData = useAuthService.getUserData();
  const [loading, setLoading] = useState(false);
  const { showLoader, hideLoader, message } = useGlobalUI();

  // Filters state
  const [filterStatus, setFilterStatus] = useState(null); // map to transactionStatus
  const [filterType, setFilterType] = useState(null); // transactionType
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);

  // Pagination state
  const [pageNo, setPageNo] = useState(0); // backend pageNo is 0-based
  const [pageSize, setPageSize] = useState(7);

  // Fetch transactions from API with filters and pagination
  const fetchTransactions = () => {
    showLoader();

    // Prepare payload matching backend filter model
    const payload = {
      pageNo,
      pageSize,
      transactionStatus: filterStatus,
      transactionType: filterType,
      searchBy: searchText || undefined,
      startDate: dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : undefined,
      endDate: dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : undefined,
      // fromUserId, toUserId can also be added here if needed

      toUserId: userData.uType === 3 ? AuthService.getAuthId() : null,
    };

    transactionFilter(payload)
      .then((res) => {
        // Backend returns a paged response:
        // Assume response shape: { content: [...transactions], totalElements: number, ... }
        const data = res.data.data || {};

        setTransactions(data.content || []);
        setTotalRecords(data.totalElements || 0);
      })
      .catch((err) => {
        message.error("Failed to fetch transactions");
        console.error(err);
      })
      .finally(() => hideLoader());
  };

  // Fetch data when filters or pagination changes
  useEffect(() => {
    fetchTransactions();
  }, [pageNo, pageSize, filterStatus, filterType, searchText, dateRange]);

  // Table columns definition
  const columns = [
    {
      title: "Transaction ID",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 140,
      render: (text) => <Text strong>{formatIdWithPrefix(text, "TXN")}</Text>,
    },
    {
      title: "Sender",
      key: "fromUser",
      width: 180,
      render: (_, record) => (
        <>
          {record.fromUser ? (
            <>
              <div>{record.fromUser?.name}</div>
              <div style={{ color: "#888" }}>{record.fromUser?.mobileNo}</div>
            </>
          ) : (
            "-"
          )}
        </>
      ),
    },
    {
      title: "Reciever",
      key: "toUser",
      width: 180,
      render: (_, record) => (
        <>
          <div>{record.toUser?.name}</div>
          <div style={{ color: "#888" }}>{record.toUser?.mobileNo}</div>
        </>
      ),
    },
    {
      title: "Amount (CAD)",
      dataIndex: "transactionAmount",
      key: "transactionAmount",
      width: 100,
      align: "right",
      render: (amount) =>
        `CA$${Number(amount || 0).toLocaleString("en-CA", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`, // assuming amount is in cents, adjust if needed
      sorter: (a, b) => a.transactionAmount - b.transactionAmount,
    },
    {
      title: "Type",
      dataIndex: "transactionType",
      key: "transactionType",
      width: 120,
      filters: [
        { text: "Credit", value: "CREDIT" },
        { text: "Debit", value: "DEBIT" },
      ],
      filteredValue: filterType ? [filterType] : null,
      onFilter: (value, record) => record.transactionType === value,
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "transactionStatus",
      key: "transactionStatus",
      width: 120,
      filters: [
        { text: "Success", value: "SUCCESS" },
        { text: "Pending", value: "PENDING" },
        { text: "Failed", value: "FAILED" },
      ],
      filteredValue: filterStatus ? [filterStatus] : null,
      onFilter: (value, record) => record.transactionStatus === value,
      render: (status) => <StatusTag status={status} />,
      align: "center",
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 180,
      sorter: (a, b) => new Date(a.createdDate) - new Date(b.createdDate),
      render: (date) => moment(date).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 200,
      ellipsis: true,
    },
  ];

  // Handle Table pagination & filters changes
  const handleTableChange = (pagination, filters) => {
    // Update page and pageSize
    setPageNo(pagination.current - 1);
    setPageSize(pagination.pageSize);

    // Update filters if changed
    if (filters.transactionStatus) {
      setFilterStatus(filters.transactionStatus[0] || null);
    }
    if (filters.transactionType) {
      setFilterType(filters.transactionType[0] || null);
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Transaction Overview
          </Title>
          <Text type="secondary">
            Monitor and manage all wallet transactions
          </Text>
        </div>

        <Space>
          <Button
            icon={<BarChartOutlined />}
            type="default"
            // You can add analytics modal open here
          >
            Analytics
          </Button>
          <Button type="default" icon={<ReloadOutlined />}>
            Export Report
          </Button>
        </Space>
      </div>

      <Card
        bordered={false}
        style={{ margin: 24, backgroundColor: "#fff" }}
        bodyStyle={{ padding: "24px" }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* Filters and Search */}
          <Space wrap size="middle" align="center" style={{ marginBottom: 12 }}>
            <Input.Search
              placeholder="Search by remarks, reference, IP, user agent..."
              allowClear
              onSearch={(val) => {
                setPageNo(0); // reset to first page
                setSearchText(val);
              }}
              style={{ width: 300 }}
            />
            <Select
              placeholder="Filter Status"
              allowClear
              value={filterStatus}
              onChange={(val) => {
                setPageNo(0);
                setFilterStatus(val);
              }}
              style={{ width: 160 }}
            >
              <Option value="SUCCESS">Success</Option>
              <Option value="PENDING">Pending</Option>
              <Option value="FAILED">Failed</Option>
            </Select>
            <Select
              placeholder="Filter Type"
              allowClear
              value={filterType}
              onChange={(val) => {
                setPageNo(0);
                setFilterType(val);
              }}
              style={{ width: 160 }}
            >
              <Option value="CREDIT">Credit</Option>
              <Option value="DEBIT">Debit</Option>
            </Select>

            <RangePicker
              onChange={(dates) => {
                setPageNo(0);
                setDateRange(dates || [null, null]);
              }}
              style={{ width: 280 }}
              allowClear
            />
          </Space>

          {/* Table */}
          <Table
            columns={columns}
            dataSource={transactions}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pageNo + 1,
              pageSize,
              total: totalRecords,
              showSizeChanger: true,
              pageSizeOptions: ["5", "7", "10", "20", "50"],
            }}
            onChange={handleTableChange}
            scroll={{ x: 1100 }}
            locale={{ emptyText: "No transactions found." }}
          />
        </Space>
      </Card>
    </>
  );
}
